import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Save, Plus, Trash2, Loader2, Settings, Megaphone, HelpCircle, Inbox, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { LOGO_URL } from "@/data/content";
import { useSEO } from "@/hooks/useSite";

const TABS = [
  { k: "config", label: "Site Settings", icon: Settings },
  { k: "announcements", label: "Announcements", icon: Megaphone },
  { k: "faqs", label: "FAQs", icon: HelpCircle },
  { k: "enquiries", label: "Enquiries", icon: Inbox },
];

const inp = "w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A1E4E] focus:ring-2 focus:ring-[#0A1E4E]/10";
const lab = "text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1 block";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { config, refresh } = useSiteConfig();
  const navigate = useNavigate();
  const [tab, setTab] = useState("config");
  useSEO({ title: "Dashboard | Admin — IAUE School of Basic Studies" });

  const doLogout = () => { logout(); navigate("/admin"); };

  return (
    <div className="min-h-screen bg-[#F1F0EA]">
      <header className="sticky top-0 z-30 bg-[#0A1E4E] text-white">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-5 py-3">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="crest" className="h-9 w-9 object-contain" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Content Management</div>
              <div className="font-serif font-bold">School of Basic Studies</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noopener noreferrer" className="hidden sm:flex items-center gap-1.5 text-sm text-blue-100/80 hover:text-white"><ExternalLink size={14} /> View Site</a>
            <span className="hidden sm:block text-sm text-blue-100/70">{user?.email}</span>
            <button onClick={doLogout} className="btn-red !py-2 !px-4 !text-xs" data-testid="admin-logout-button"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-6">
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
              className={`inline-flex items-center gap-2 rounded-sm px-4 py-2.5 text-sm font-semibold transition-all ${tab === t.k ? "bg-[#0A1E4E] text-[#D4AF37]" : "bg-white text-slate-600 border border-slate-200 hover:border-[#0A1E4E]"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "config" && <ConfigTab config={config} refresh={refresh} />}
        {tab === "announcements" && <AnnouncementsTab />}
        {tab === "faqs" && <FaqsTab />}
        {tab === "enquiries" && <EnquiriesTab />}
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-6">
      {title && <h3 className="font-serif text-lg font-bold text-[#0A1E4E] mb-4">{title}</h3>}
      {children}
    </div>
  );
}

function ConfigTab({ config, refresh }) {
  const [form, setForm] = useState(config);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(config); }, [config]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPhones = (e) => setForm((f) => ({ ...f, phones: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/config", form);
      await refresh();
      toast.success("Site settings updated — changes are live.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-config-panel">
      <Card title="Academic Session & Admission Status">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={lab}>Academic Session</label><input className={inp} value={form.academicSession || ""} onChange={set("academicSession")} data-testid="config-session" /></div>
          <div>
            <label className={lab}>Admission Status</label>
            <select className={inp} value={form.admissionStatus || "OPEN"} onChange={set("admissionStatus")} data-testid="admin-toggle-admission-status">
              <option value="OPEN">Admissions Open</option>
              <option value="CLOSING_SOON">Applications Closing Soon</option>
              <option value="CLOSED">Applications Closed</option>
              <option value="COMING_SOON">Next Session Coming Soon</option>
            </select>
          </div>
          <div><label className={lab}>Application Deadline</label><input className={inp} value={form.deadline || ""} onChange={set("deadline")} data-testid="config-deadline" /></div>
          <div><label className={lab}>Banner CTA Label</label><input className={inp} value={form.bannerCtaLabel || ""} onChange={set("bannerCtaLabel")} /></div>
          <div className="sm:col-span-2"><label className={lab}>Banner / Status Text</label><textarea rows={2} className={inp} value={form.bannerText || ""} onChange={set("bannerText")} data-testid="config-banner" /></div>
          <div className="sm:col-span-2"><label className={lab}>Application URL (leave blank to use the enquiry form)</label><input className={inp} value={form.applicationUrl || ""} onChange={set("applicationUrl")} placeholder="https://portal.example.com/apply" data-testid="config-apply-url" /></div>
        </div>
      </Card>

      <Card title="Fees">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={lab}>JUPEB Fees</label><textarea rows={2} className={inp} value={form.feesJupeb || ""} onChange={set("feesJupeb")} /></div>
          <div><label className={lab}>IJMB Fees</label><textarea rows={2} className={inp} value={form.feesIjmb || ""} onChange={set("feesIjmb")} /></div>
        </div>
      </Card>

      <Card title="Contact Information">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className={lab}>Primary Phone</label><input className={inp} value={form.phonePrimary || ""} onChange={set("phonePrimary")} /></div>
          <div><label className={lab}>WhatsApp (with country code)</label><input className={inp} value={form.whatsapp || ""} onChange={set("whatsapp")} /></div>
          <div className="sm:col-span-2"><label className={lab}>Other Phones (comma separated)</label><input className={inp} value={(form.phones || []).join(", ")} onChange={setPhones} /></div>
          <div><label className={lab}>Email</label><input className={inp} value={form.email || ""} onChange={set("email")} /></div>
          <div><label className={lab}>Website</label><input className={inp} value={form.website || ""} onChange={set("website")} /></div>
          <div className="sm:col-span-2"><label className={lab}>Address</label><input className={inp} value={form.address || ""} onChange={set("address")} /></div>
        </div>
      </Card>

      <Card title="Mission & Vision">
        <div className="grid gap-4">
          <div><label className={lab}>Mission</label><textarea rows={2} className={inp} value={form.mission || ""} onChange={set("mission")} /></div>
          <div><label className={lab}>Vision</label><textarea rows={2} className={inp} value={form.vision || ""} onChange={set("vision")} /></div>
        </div>
      </Card>

      <button className="btn-navy" onClick={save} disabled={saving} data-testid="config-save-button">
        {saving ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : <><Save size={16} /> Save Changes</>}
      </button>
    </div>
  );
}

function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", category: "General", pinned: false });
  const load = () => api.get("/announcements").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title is required.");
    try { await api.post("/announcements", form); setForm({ title: "", body: "", category: "General", pinned: false }); await load(); toast.success("Announcement published."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this announcement? This cannot be undone.")) return; await api.delete(`/announcements/${id}`); await load(); toast.success("Deleted."); };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-announcements-panel">
      <Card title="New Announcement">
        <div className="space-y-3">
          <div><label className={lab}>Title</label><input className={inp} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ann-title" /></div>
          <div><label className={lab}>Category</label><input className={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
          <div><label className={lab}>Body</label><textarea rows={4} className={inp} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="ann-body" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} /> Pin to top</label>
          <button className="btn-navy w-full" onClick={create} data-testid="ann-create-button"><Plus size={16} /> Publish</button>
        </div>
      </Card>
      <Card title={`Published (${items.length})`}>
        <div className="space-y-3 max-h-[32rem] overflow-y-auto">
          {items.map((a) => (
            <div key={a.id} className="rounded-sm border border-slate-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs text-slate-400">{a.category} • {a.date}{a.pinned ? " • Pinned" : ""}</div>
                  <div className="font-bold text-[#0A1E4E]">{a.title}</div>
                  <p className="text-sm text-slate-600 line-clamp-2">{a.body}</p>
                </div>
                <button onClick={() => remove(a.id)} className="text-[#C0202A] p-1" data-testid={`ann-delete-${a.id}`}><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function FaqsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ question: "", answer: "", category: "General", order: 0 });
  const load = () => api.get("/faqs").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.question.trim()) return toast.error("Question is required.");
    try { await api.post("/faqs", { ...form, order: Number(form.order) || items.length + 1 }); setForm({ question: "", answer: "", category: "General", order: 0 }); await load(); toast.success("FAQ added."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this FAQ? This cannot be undone.")) return; await api.delete(`/faqs/${id}`); await load(); toast.success("Deleted."); };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="admin-faqs-panel">
      <Card title="New FAQ">
        <div className="space-y-3">
          <div><label className={lab}>Question</label><input className={inp} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-question" /></div>
          <div><label className={lab}>Answer</label><textarea rows={4} className={inp} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="faq-answer" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lab}>Category</label><input className={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div><label className={lab}>Order</label><input type="number" className={inp} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
          </div>
          <button className="btn-navy w-full" onClick={create} data-testid="faq-create-button"><Plus size={16} /> Add FAQ</button>
        </div>
      </Card>
      <Card title={`FAQs (${items.length})`}>
        <div className="space-y-3 max-h-[32rem] overflow-y-auto">
          {items.map((f) => (
            <div key={f.id} className="rounded-sm border border-slate-200 p-4 flex items-start justify-between gap-3">
              <div><div className="font-bold text-[#0A1E4E]">{f.question}</div><p className="text-sm text-slate-600 line-clamp-2">{f.answer}</p></div>
              <button onClick={() => remove(f.id)} className="text-[#C0202A] p-1" data-testid={`faq-delete-${f.id}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function EnquiriesTab() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const load = () => api.get("/enquiries").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => { await api.patch(`/enquiries/${id}`, { status }); await load(); };
  const remove = async (id) => { if (!window.confirm("Delete this enquiry?")) return; await api.delete(`/enquiries/${id}`); await load(); toast.success("Deleted."); };
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || "—"; } };

  const DETAILS = [
    ["Email", "email"], ["WhatsApp", "whatsapp"], ["Date of Birth", "dob"], ["Gender", "gender"],
    ["State of Origin", "stateOfOrigin"], ["Preferred University", "preferredUniversity"],
    ["O'Level Status", "olevelStatus"], ["UTME Status", "utmeStatus"],
  ];

  return (
    <Card title={`Submitted Enquiries (${items.length})`}>
      <div className="overflow-x-auto" data-testid="admin-enquiries-panel">
        {items.length === 0 ? <p className="text-slate-500 py-8 text-center">No enquiries yet.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Phone</th><th className="py-2 pr-4">Programme</th><th className="py-2 pr-4">Course</th><th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Status</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {items.map((e) => (
                <Fragment key={e.id}>
                  <tr className="border-b border-slate-100" data-testid={`enquiry-row-${e.id}`}>
                    <td className="py-2.5 pr-4">
                      <button onClick={() => setExpanded(expanded === e.id ? null : e.id)} className="font-medium text-[#0A1E4E] hover:underline" data-testid={`enquiry-expand-${e.id}`}>{e.fullName}</button>
                    </td>
                    <td className="py-2.5 pr-4"><a href={`tel:${e.phone}`} className="text-slate-700 hover:text-[#0A1E4E]">{e.phone}</a></td>
                    <td className="py-2.5 pr-4">{e.track || "—"}</td>
                    <td className="py-2.5 pr-4">{e.intendedCourse || "—"}</td>
                    <td className="py-2.5 pr-4 text-slate-500 whitespace-nowrap">{fmtDate(e.createdAt)}</td>
                    <td className="py-2.5 pr-4">
                      <select value={e.status} onChange={(ev) => setStatus(e.id, ev.target.value)} className="rounded-sm border border-slate-300 px-2 py-1 text-xs">
                        <option value="new">New</option><option value="contacted">Contacted</option><option value="enrolled">Enrolled</option><option value="closed">Closed</option>
                      </select>
                    </td>
                    <td className="py-2.5"><button onClick={() => remove(e.id)} className="text-[#C0202A]" data-testid={`enquiry-delete-${e.id}`}><Trash2 size={15} /></button></td>
                  </tr>
                  {expanded === e.id && (
                    <tr className="bg-[#F7F5EF]" data-testid={`enquiry-detail-${e.id}`}>
                      <td colSpan={7} className="p-5">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                          {DETAILS.map(([label, key]) => (
                            <div key={key}><div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div><div className="text-slate-800">{e[key] || "—"}</div></div>
                          ))}
                        </div>
                        {e.message && <div className="mt-3"><div className="text-[11px] uppercase tracking-wide text-slate-400">Message</div><p className="text-slate-800">{e.message}</p></div>}
                        <div className="mt-3 flex gap-2">
                          <a href={`tel:${e.phone}`} className="btn-navy !py-1.5 !px-3 !text-xs">Call</a>
                          {e.whatsapp && <a href={`https://wa.me/${e.whatsapp.replace(/[^0-9]/g,'')}`} target="_blank" rel="noopener noreferrer" className="btn-gold !py-1.5 !px-3 !text-xs">WhatsApp</a>}
                          {e.email && <a href={`mailto:${e.email}`} className="btn-red !py-1.5 !px-3 !text-xs">Email</a>}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
}
